import { useEffect, useState, type RefObject } from 'react';

import { averageSpineWidth, type Density } from './spine-utils.js';

/**
 * Capacité en spines d'un shelf — calculée d'après la **largeur réelle** observée
 * via ResizeObserver. Plus aucun viewport-breakpoint : ce qui compte est ce que
 * le shelf peut **vraiment** afficher.
 *
 * Le padding du shelf (≈ 28 px), l'espace réservé aux décorations (~70 px pour
 * 2 décos avg) et une marge de sécurité sont soustraits.
 */
export function useShelfCapacity(
  ref: RefObject<HTMLElement>,
  density: Density = 'comfy',
): number {
  const [capacity, setCapacity] = useState<number>(() =>
    density === 'compact' ? 30 : 16,
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const recompute = (width: number): void => {
      const usable = Math.max(0, width - 28 - 70);
      const next = Math.max(4, Math.floor(usable / averageSpineWidth(density)));
      setCapacity(next);
    };

    recompute(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) recompute(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, density]);

  return capacity;
}
